/* eslint-disable max-len */
import { Col, Row, Space } from 'antd';
import { type FormikHelpers, useFormikContext } from 'formik';
import { head } from 'lodash/fp';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { type EventDataFragment } from '../../../../api/fragments/EventData';
import { AssetCondition, CompanyTheme, CustomerKind } from '../../../../api/types';
import CheckboxField from '../../../../components/fields/ci/CheckboxField';
import { defaultFilterOption } from '../../../../components/fields/SelectField';
import { useThemeComponents } from '../../../../themes';
import useVehicleOptions, { type LocalVariant, type Option } from '../../../../utilities/useVehicleOptions';
import { hasAppointmentScenario } from '../../../admin/ModuleDetailsPage/modules/implementations/shared';
import DealerSelection from '../../ConfiguratorApplicationEntrypoint/ModelConfiguratorDetailsPage/components/DealerSelection';
import AppointmentDetailsSection from './AppointmentDetailsSection';
import { type ApplicantFormValues, Title } from './shared';

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    font-size: 16px;

    & .ant-form-item {
        margin-bottom: 0;
    }

    & .ant-typography {
        margin-left: 0.5rem;
    }
`;

const SelectionContainer = styled.div<{ $theme: CompanyTheme }>`
    background-color: #eff0f1;
    padding: 20px 24px 0 24px;
    border-radius: var(--card-border-radius, initial);

    ${({ $theme }) =>
        $theme === CompanyTheme.Porsche &&
        css`
            .ant-select-selector {
                background-color: #fff !important;
            }

            .ant-input:not(.ant-input-disabled) {
                background-color: #fff !important;
            }

            .ant-select-disabled > .ant-select-selector {
                background-color: #f5f5f5 !important;
            }
        `}
`;

export const useAssetConditionOptions = () => {
    const { t } = useTranslation('common');

    return useMemo(
        () => [
            { label: t('common:options.assetCondition.new'), value: AssetCondition.New },
            { label: t('common:options.assetCondition.preOwned'), value: AssetCondition.Used },
        ],
        [t]
    );
};

const useReset = (
    initialValues: ApplicantFormValues,
    values: ApplicantFormValues,
    modelOptions: Option[],
    subModelOptions: Option[],
    vehicleOptions: Option[],
    setFieldValue: FormikHelpers<ApplicantFormValues>['setFieldValue']
) => {
    const previousDealerIdRef = useRef(initialValues.dealerId);
    useEffect(() => {
        // check whenever dealer is changed
        if (previousDealerIdRef.current !== values.dealerId) {
            previousDealerIdRef.current = values.dealerId;
            const { model, subModel, variant } = values.vehicleInterest;
            if (!modelOptions.some(option => option.value === model)) {
                setFieldValue('vehicleInterest.model', undefined);
                setFieldValue('vehicleInterest.subModel', undefined);
                setFieldValue('vehicleInterest.variant', undefined);
            } else if (!subModelOptions.some(option => option.value === subModel)) {
                setFieldValue('vehicleInterest.subModel', undefined);
                setFieldValue('vehicleInterest.variant', undefined);
            } else if (!vehicleOptions.some(option => option.value === variant)) {
                setFieldValue('vehicleInterest.variant', undefined);
            }
        }
    }, [values.dealerId, setFieldValue, values.vehicleInterest, modelOptions, subModelOptions, vehicleOptions]);
};

export type VehicleInterestProps = {
    variants: LocalVariant[];
    event: EventDataFragment;
    customerKind: CustomerKind;
};

const colSpan = { span: 24 };

const VehicleInterest = ({ variants: moduleVariants, event, customerKind }: VehicleInterestProps) => {
    const { t } = useTranslation('eventApplicantForm');
    const { getAvailableModelOptions, getAvailableSubModelOptions, getAvailableVehicleOptions } = useVehicleOptions();
    const { values, setFieldValue, initialValues } = useFormikContext<ApplicantFormValues>();
    const {
        theme,
        Checkbox,
        FormFields: { SelectField },
    } = useThemeComponents();

    const variants = useMemo(() => {
        const dealerVehicles = event.dealerVehicles?.find(vehicle => vehicle.dealerId === values.dealerId);

        return (moduleVariants || [])
            .filter(variant => dealerVehicles?.vehicleSuiteIds.includes(variant.versioning.suiteId))
            .filter(Boolean);
    }, [moduleVariants, values.dealerId, event.dealerVehicles]);

    const [modelOptions, subModelOptions, vehicleOptions] = useMemo(() => {
        const modelOptions = getAvailableModelOptions(variants);
        const subModelOptions = getAvailableSubModelOptions(variants, modelOptions, values.vehicleInterest.model);
        const vehicleOptions = getAvailableVehicleOptions(
            variants,
            values.vehicleInterest.model,
            values.vehicleInterest.subModel
        );

        return [modelOptions, subModelOptions, vehicleOptions];
    }, [
        getAvailableModelOptions,
        getAvailableSubModelOptions,
        getAvailableVehicleOptions,
        values.vehicleInterest.model,
        values.vehicleInterest.subModel,
        variants,
    ]);

    useReset(initialValues, values, modelOptions, subModelOptions, vehicleOptions, setFieldValue);
    useEffect(() => {
        if (!values.vehicleInterest.model && modelOptions.length > 0) {
            setFieldValue('vehicleInterest.model', head(modelOptions).value);
        }
    }, [modelOptions, setFieldValue, values.vehicleInterest.model]);

    // watch model or sub model change event
    const previousModelRef = useRef(initialValues.vehicleInterest?.model);
    const previousSubModelRef = useRef(initialValues.vehicleInterest?.subModel);
    useEffect(() => {
        if (
            values.vehicleInterest.model !== previousModelRef.current ||
            values.vehicleInterest.subModel !== previousSubModelRef.current
        ) {
            previousModelRef.current = values.vehicleInterest.model;
            previousSubModelRef.current = values.vehicleInterest.subModel;

            const subModels = getAvailableSubModelOptions(variants, modelOptions, values.vehicleInterest.model);

            // init selected sub model is empty
            let subModelId = null;
            if (subModels?.length) {
                // if there is new sub models, give it current selected sub model
                subModelId = values.vehicleInterest.subModel;

                // if did find current selected sub model in new sub models
                // mean changed model cause sub model list changed
                // so need to put first new sub model as default selected sub model
                const find = subModels.find(i => i.value === subModelId);
                if (!find) {
                    subModelId = head(subModels)?.value;
                    setFieldValue('vehicleInterest.subModel', subModelId);
                }
            } else {
                setFieldValue('vehicleInterest.subModel', subModelId);
            }

            // Do the same thing with variant
            const vehicles = getAvailableVehicleOptions(variants, values.vehicleInterest.model, subModelId);
            let variantVehicleId = null;
            if (vehicles?.length) {
                variantVehicleId = values.vehicleInterest.variant ?? initialValues.vehicleInterest.variant;

                const find = vehicles.find(i => i.value === variantVehicleId);
                if (!find) {
                    variantVehicleId = head(vehicles)?.value;
                    setFieldValue('vehicleInterest.variant', variantVehicleId);
                }
            } else {
                setFieldValue('vehicleInterest.variant', variantVehicleId);
            }
        }
    }, [
        modelOptions,
        values.vehicleInterest.model,
        values.vehicleInterest.subModel,
        values.vehicleInterest.variant,
        variants,
        getAvailableSubModelOptions,
        getAvailableVehicleOptions,
        setFieldValue,
        initialValues.vehicleInterest.variant,
    ]);

    return (
        <Col span={24}>
            <Row gutter={[24, 24]}>
                <Col {...colSpan}>
                    <SelectionContainer $theme={theme}>
                        <Title>{t('eventApplicantForm:vehicleOfInterest.title')}</Title>
                        <Row gutter={24}>
                            {event.showDealership && (
                                <Col span={24}>
                                    <DealerSelection
                                        {...t('eventApplicantForm:fields.preferredDealership', { returnObjects: true })}
                                        data-cy="vehicleInterestDealer"
                                        dealers={event.dealers?.map(({ id, displayName, __typename, legalName }) => ({
                                            __typename,
                                            id,
                                            displayName,
                                            legalName,
                                        }))}
                                        disabled={customerKind === CustomerKind.Guarantor}
                                        showOnlyWhenMoreThanOne={false}
                                        required
                                    />
                                </Col>
                            )}
                            <Col span={24}>
                                <SelectField
                                    {...t('eventApplicantForm:fields.selectModel', { returnObjects: true })}
                                    data-cy="vehicleInterestModel"
                                    disabled={customerKind === CustomerKind.Guarantor}
                                    filterOption={defaultFilterOption}
                                    name="vehicleInterest.model"
                                    options={modelOptions}
                                    required
                                    showSearch
                                />
                            </Col>
                            {subModelOptions.length > 0 && (
                                <Col span={24}>
                                    <SelectField
                                        {...t('eventApplicantForm:fields.selectSubModel', { returnObjects: true })}
                                        data-cy="vehicleInterestSubmodel"
                                        disabled={customerKind === CustomerKind.Guarantor}
                                        filterOption={defaultFilterOption}
                                        name="vehicleInterest.subModel"
                                        options={subModelOptions}
                                        required
                                        showSearch
                                    />
                                </Col>
                            )}
                            <Col span={24}>
                                <SelectField
                                    {...t('eventApplicantForm:fields.selectVariant', { returnObjects: true })}
                                    data-cy="vehicleInterestVariant"
                                    disabled={customerKind === CustomerKind.Guarantor}
                                    filterOption={defaultFilterOption}
                                    name="vehicleInterest.variant"
                                    options={vehicleOptions}
                                    required
                                    showSearch
                                />
                            </Col>
                        </Row>
                    </SelectionContainer>
                </Col>

                {customerKind !== CustomerKind.Guarantor && (event.isAllowTradeIn || event.isAllowTestDrive) && (
                    <Col {...colSpan}>
                        <Space direction="vertical" size={8}>
                            {event.isAllowTestDrive && (
                                <CheckboxContainer>
                                    <CheckboxField
                                        customComponent={Checkbox}
                                        disabled={
                                            event.scenarios.length === 1 && hasAppointmentScenario(event.scenarios)
                                        }
                                        name="configuration.testDrive"
                                    >
                                        {t('eventApplicantForm:fields.requestTestDrive.label')}
                                    </CheckboxField>
                                </CheckboxContainer>
                            )}
                            {event.isAllowTradeIn && (
                                <CheckboxContainer>
                                    <CheckboxField customComponent={Checkbox} name="configuration.tradeIn">
                                        {t('eventApplicantForm:fields.tradeIn.label')}
                                    </CheckboxField>
                                </CheckboxContainer>
                            )}
                        </Space>
                    </Col>
                )}

                {customerKind !== CustomerKind.Guarantor &&
                    values.configuration.testDrive &&
                    hasAppointmentScenario(event.scenarios) &&
                    event.isAllowTestDrive &&
                    event.module.__typename === 'EventApplicationModule' &&
                    event.module.appointmentModule &&
                    event.module.displayAppointmentDatepicker && (
                        <Col {...colSpan}>
                            <AppointmentDetailsSection
                                applicationModule={event.module}
                                showSkipValidation={event.privateAccess}
                            />
                        </Col>
                    )}
            </Row>
        </Col>
    );
};

export default VehicleInterest;
